/**
 * Profiles management page
 * 
 * Per design.md: Use @uprosal/ui components, semantic tokens, lucide icons
 * Connected to Convex backend for real CRUD operations.
 */

import { useState } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from 'convex/_generated/api';
import type { Doc } from 'convex/_generated/dataModel';
import { motion } from 'framer-motion';
import {
    Card, CardContent, CardHeader, CardTitle,
    Button, Badge, Input, Skeleton,
    Plus, Search, User, Trash2, Edit, ExternalLink, MapPin, Paperclip,
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose,
} from '@uprosal/ui';
import { ProfileDialog } from './ProfileDialog';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { AttachmentsSection } from './AttachmentsSection';

type Profile = Doc<'connectedProfiles'>;

export function ProfilesPage() {
    const [searchQuery, setSearchQuery] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingProfile, setEditingProfile] = useState<Profile | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<Profile | null>(null);
    const [attachmentsProfile, setAttachmentsProfile] = useState<Profile | null>(null);

    // Fetch profiles from Convex
    const profiles = useQuery(api.queries.profiles.listConnected, {}) as Profile[] | undefined;
    const removeProfile = useMutation(api.mutations.profiles.remove);

    // Filter profiles by search query
    const filteredProfiles = profiles?.filter((p: Profile) =>
        p.profileLabel.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (p.title?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false)
    ) ?? [];

    const handleNewProfile = () => {
        setEditingProfile(null);
        setDialogOpen(true);
    };

    const handleEditProfile = (profile: Profile) => {
        setEditingProfile(profile);
        setDialogOpen(true);
    };

    const handleDeleteProfile = (profile: Profile) => {
        setDeleteConfirm(profile);
    };

    const confirmDelete = async () => {
        if (!deleteConfirm) return;
        try {
            await removeProfile({ profileId: deleteConfirm._id });
        } catch (err) {
            console.error('Failed to delete profile:', err);
        }
        setDeleteConfirm(null);
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">Connected Profiles</h1>
                    <p className="text-muted-foreground mt-1">
                        Connect your Upwork profiles for personalized AI analysis
                    </p>
                </div>
                <Button className="flex items-center gap-2" onClick={handleNewProfile}>
                    <Plus className="w-4 h-4" />
                    Connect Profile
                </Button>
            </div>

            {/* Search */}
            {profiles && profiles.length > 0 && (
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            type="text"
                            placeholder="Search profiles..."
                            className="w-full pl-10"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>
            )}

            {/* Loading State */}
            {profiles === undefined && (
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {[1, 2, 3].map((i) => (
                        <Card key={i} className="bg-card border-border">
                            <CardContent className="p-6">
                                <Skeleton className="h-12 w-12 rounded-full mb-4" />
                                <Skeleton className="h-6 w-3/4 mb-2" />
                                <Skeleton className="h-4 w-1/2 mb-4" />
                                <div className="flex gap-2">
                                    <Skeleton className="h-6 w-16" />
                                    <Skeleton className="h-6 w-16" />
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Empty State */}
            {profiles?.length === 0 && (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center py-16"
                >
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                        <User className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                        No profiles connected
                    </h3>
                    <p className="text-muted-foreground max-w-md mx-auto mb-6">
                        Connect your Upwork profile to enable AI-powered job analysis and personalized proposal generation.
                    </p>
                    <Button onClick={handleNewProfile} className="gap-2">
                        <Plus className="w-4 h-4" />
                        Connect Your First Profile
                    </Button>
                </motion.div>
            )}

            {/* Profile Cards */}
            {filteredProfiles.length > 0 && (
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {filteredProfiles.map((profile, index) => (
                        <motion.div
                            key={profile._id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                        >
                            <Card className="bg-card border-border hover:border-primary/50 transition-colors group">
                                <CardHeader className="pb-3">
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                                <User className="w-5 h-5 text-primary" />
                                            </div>
                                            <div>
                                                <CardTitle className="text-base font-semibold text-foreground">
                                                    {profile.profileLabel}
                                                </CardTitle>
                                                {profile.title && (
                                                    <p className="text-sm text-muted-foreground line-clamp-1">
                                                        {profile.title}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8"
                                                onClick={() => handleEditProfile(profile)}
                                            >
                                                <Edit className="w-4 h-4 text-muted-foreground" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8"
                                                onClick={() => handleDeleteProfile(profile)}
                                            >
                                                <Trash2 className="w-4 h-4 text-destructive" />
                                            </Button>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="pt-0">
                                    {/* Country */}
                                    {profile.country && (
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                                            <MapPin className="w-4 h-4" />
                                            {profile.country}
                                        </div>
                                    )}

                                    {/* Skills */}
                                    {profile.skills.length > 0 && (
                                        <div className="flex flex-wrap gap-1.5 mb-4">
                                            {profile.skills.slice(0, 5).map((skill: string) => (
                                                <Badge key={skill} variant="secondary" className="text-xs">
                                                    {skill}
                                                </Badge>
                                            ))}
                                            {profile.skills.length > 5 && (
                                                <Badge variant="outline" className="text-xs text-muted-foreground">
                                                    +{profile.skills.length - 5} more
                                                </Badge>
                                            )}
                                        </div>
                                    )}

                                    {/* View on Upwork link */}
                                    <div className="flex items-center gap-4">
                                        <a
                                            href={profile.upworkProfileUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                                        >
                                            <ExternalLink className="w-3.5 h-3.5" />
                                            View on Upwork
                                        </a>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-7 px-2 text-xs gap-1"
                                            onClick={() => setAttachmentsProfile(profile)}
                                        >
                                            <Paperclip className="w-3.5 h-3.5" />
                                            Attachments
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </div>
            )}

            {/* No search results */}
            {profiles && profiles.length > 0 && filteredProfiles.length === 0 && searchQuery && (
                <div className="text-center py-12">
                    <p className="text-muted-foreground">
                        No profiles found matching "{searchQuery}"
                    </p>
                </div>
            )}

            {/* Profile Dialog */}
            <ProfileDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                editProfile={editingProfile}
            />

            {/* Delete Confirmation */}
            <ConfirmDialog
                open={deleteConfirm !== null}
                onOpenChange={(open) => !open && setDeleteConfirm(null)}
                title="Delete Profile"
                description={`Are you sure you want to delete "${deleteConfirm?.profileLabel}"? This action cannot be undone.`}
                onConfirm={confirmDelete}
                confirmLabel="Delete"
                variant="destructive"
            />

            {/* Attachments Dialog */}
            <Dialog open={attachmentsProfile !== null} onOpenChange={(open) => !open && setAttachmentsProfile(null)}>
                <DialogContent className="w-[800px] max-w-[95vw] max-h-[85vh] overflow-y-auto">
                    <DialogClose onClick={() => setAttachmentsProfile(null)} />
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Paperclip className="w-5 h-5" />
                            Attachments for {attachmentsProfile?.profileLabel}
                        </DialogTitle>
                    </DialogHeader>
                    {attachmentsProfile && (
                        <AttachmentsSection
                            profileId={attachmentsProfile._id}
                            profileLabel={attachmentsProfile.profileLabel}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
